import { useEffect, useState } from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { HERO_MEDIA } from "../../constants/collect";
import Images from "../../utils/Images";
import { ArrowIcon, BrandMark, EASE, useFade } from "./ui";

const PULSE = [
  { n: "01", title: "Onboard", line: "Portfolios land, validate and become recovery cases." },
  { n: "02", title: "Engage", line: "SMS, WhatsApp, email, USSD and payment links go out on rules." },
  { n: "03", title: "Recover", line: "Mobile Money, USSD and links settle — then reconcile automatically." },
  { n: "04", title: "Report", line: "Executives watch recovery, ageing and collector productivity live." },
];

const COVERS = [
  "Loans and receivables",
  "Insurance premiums",
  "Hire purchase agreements",
  "Mobile Money, USSD and payment links",
];

function CollectHero() {
  const { reduce } = useFade();
  const [active, setActive] = useState(0);
  const fadeUp = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0 } : { duration: 0.55, ease: EASE },
    },
  };

  useEffect(() => {
    if (reduce) return undefined;
    const id = window.setInterval(() => {
      setActive((step) => (step + 1) % PULSE.length);
    }, 3400);
    return () => window.clearInterval(id);
  }, [reduce]);

  return (
    <section
      id="simcollect-hero"
      data-tone="paper"
      className="relative flex min-h-[100svh] flex-col overflow-hidden bg-[#faf6f4] text-navy"
      aria-labelledby="simcollect-heading"
    >
      <HeroAtmosphere src={HERO_MEDIA.src} alt={HERO_MEDIA.alt} spec={HERO_MEDIA.spec} />

      <div className="relative z-[2] flex min-h-[100svh] flex-1 flex-col lg:flex-row">
        <nav
          className="relative z-[3] hidden w-[5.75rem] shrink-0 flex-col border-r border-navy/10 bg-white pt-[5.5rem] text-navy lg:flex"
          aria-label="Recovery stages"
        >
          {PULSE.map((item, index) => {
            const current = index === active;
            return (
              <button
                key={item.n}
                type="button"
                onClick={() => setActive(index)}
                onMouseEnter={() => setActive(index)}
                className={`relative flex min-h-0 flex-1 cursor-pointer flex-col items-center justify-center gap-2 px-2 text-center transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-collect-gold ${
                  current ? "bg-[#faf6f4] text-navy" : "text-navy/45 hover:bg-[#faf6f4] hover:text-navy"
                }`}
                aria-current={current ? "true" : undefined}
              >
                {current ? (
                  <span className="absolute inset-y-3 right-0 w-[3px] bg-collect-red" aria-hidden="true" />
                ) : null}
                <span className={`font-mono text-[10px] tracking-[0.18em] ${current ? "text-collect-red" : "text-navy/35"}`}>
                  {item.n}
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-[0.16em] [writing-mode:vertical-rl] rotate-180">
                  {item.title}
                </span>
              </button>
            );
          })}
        </nav>

        <div className="flex min-w-0 flex-1 flex-col">
          <motion.div
            className="flex flex-1 flex-col px-4 pt-[5.5rem] sm:px-6 lg:px-10 lg:pt-24"
            initial="hidden"
            animate="show"
            variants={{
              hidden: {},
              show: {
                transition: {
                  staggerChildren: reduce ? 0 : 0.08,
                  delayChildren: reduce ? 0 : 0.05,
                },
              },
            }}
          >
            <motion.div className="flex flex-wrap items-center justify-between gap-4" variants={fadeUp}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/45">
                <Link
                  to="/"
                  className="transition hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-collect-gold"
                >
                  Home
                </Link>
                <span className="mx-2 text-navy/20">/</span>
                Services
                <span className="mx-2 text-navy/20">/</span>
                SimCollect
              </p>
              <BrandMark
                src={Images.simcollect_logo}
                alt="SimCollect"
                className="h-8 w-auto max-w-[12rem] object-contain object-right sm:h-9"
              />
            </motion.div>

            <div className="mt-10 grid flex-1 items-end gap-8 pb-8 lg:mt-14 lg:grid-cols-[minmax(0,1fr)_16.5rem] lg:gap-12">
              <div className="min-w-0 max-w-3xl">
                <motion.p
                  className="text-[11px] font-semibold uppercase tracking-[0.32em] text-collect-red"
                  variants={fadeUp}
                >
                  Intelligent Digital Debt Recovery &amp; Collection Management
                </motion.p>
                <motion.h1
                  id="simcollect-heading"
                  className="mt-5 font-semibold leading-[0.98] tracking-tight [font-size:clamp(2.4rem,7vw,5.4rem)]"
                  variants={fadeUp}
                >
                  Recover Smarter.
                  <span className="mt-1 block text-collect-red">Engage Better.</span>
                  <span className="mt-1 block">Protect Your Portfolio.</span>
                </motion.h1>
                <motion.p
                  className="mt-6 max-w-2xl text-base leading-relaxed text-navy/70 sm:text-lg"
                  variants={fadeUp}
                >
                  Africa&apos;s intelligent debt recovery &amp; customer engagement platform —
                  automating the entire recovery journey through intelligence, digital engagement
                  and responsible collections.
                </motion.p>
                <motion.p
                  className="mt-5 max-w-xl border-l-[3px] border-collect-gold bg-white/55 py-2 pl-4 pr-3 text-sm leading-relaxed text-navy/65 backdrop-blur-[2px]"
                  variants={fadeUp}
                  aria-live="polite"
                >
                  <span className="font-mono text-[11px] tracking-[0.18em] text-collect-red">
                    {PULSE[active].n}
                  </span>
                  <span className="ml-3">{PULSE[active].line}</span>
                </motion.p>
              </div>

              <motion.aside
                className="shrink-0 border border-white/70 bg-white/78 p-5 shadow-[0_24px_60px_-28px_rgba(16,14,52,0.45)] backdrop-blur-md sm:p-6"
                variants={fadeUp}
                aria-label="What SimCollect covers"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/40">
                  Built to recover
                </p>
                <p className="mt-3 text-lg font-semibold leading-snug tracking-tight text-navy">
                  The complete recovery lifecycle — not reminders alone.
                </p>
                <p className="mt-2 text-sm leading-relaxed text-navy/55">
                  Workflow, engagement, payments and reporting on one enterprise desk.
                </p>
                <ul className="mt-5 space-y-3 border-t border-navy/10 pt-5">
                  {COVERS.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm font-semibold leading-snug text-navy/80">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-collect-red" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.aside>
            </div>
          </motion.div>

          <div className="relative z-[3] mt-auto border-t border-navy/10 bg-white/82 backdrop-blur-md">
            <div className="flex flex-col gap-4 px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-10">
              <ol className="flex gap-1.5 lg:hidden" aria-label="Recovery stages">
                {PULSE.map((item, index) => (
                  <li key={item.n} className="flex-1">
                    <button
                      type="button"
                      onClick={() => setActive(index)}
                      className={`flex min-h-11 w-full cursor-pointer flex-col items-start justify-center px-2.5 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-collect-gold ${
                        index === active ? "bg-collect-red text-white" : "bg-[#faf6f4] text-navy/70"
                      }`}
                    >
                      <span className="font-mono text-[9px] tracking-[0.14em] opacity-70">{item.n}</span>
                      <span className="text-[11px] font-semibold">{item.title}</span>
                    </button>
                  </li>
                ))}
              </ol>

              <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center">
                <a
                  href="#demo"
                  className="inline-flex h-12 w-full shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-collect-red px-6 text-sm font-semibold tracking-tight text-white shadow-[0_12px_28px_-12px_rgba(239,68,68,0.85)] transition hover:bg-[#dc3333] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-collect-red focus-visible:ring-offset-2 sm:w-auto"
                >
                  Request a Live Demo
                  <ArrowIcon className="h-3.5 w-3.5" />
                </a>
                <a
                  href="#videos"
                  className="inline-flex h-12 w-full shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-collect-gold px-6 text-sm font-semibold tracking-tight text-navy shadow-[0_12px_28px_-12px_rgba(229,184,11,0.85)] transition hover:bg-[#f0c52a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-collect-gold focus-visible:ring-offset-2 sm:w-auto"
                >
                  <Play className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
                  Watch Product Video
                </a>
              </div>

              <ul className="flex flex-wrap items-center gap-x-5 gap-y-2">
                {[
                  { href: "#sales", label: "Talk to Sales" },
                  { href: "#appointment", label: "Book Consultation" },
                  { href: "#pricing", label: "Request Pricing" },
                ].map((item) => (
                  <li key={item.href}>
                    <a
                      href={item.href}
                      className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold text-navy/65 transition hover:text-collect-red focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-collect-gold"
                    >
                      {item.label}
                      <ArrowIcon className="h-3 w-3" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroAtmosphere({ src, alt, spec }) {
  const [failed, setFailed] = useState(!src);

  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden={failed ? undefined : "true"}>
      {!failed ? (
        <img
          src={src}
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-[78%_center]"
          onError={() => setFailed(true)}
        />
      ) : (
        <div
          className="absolute inset-y-0 right-0 hidden w-[min(46%,38rem)] lg:block"
          role="img"
          aria-label={`${alt}. ${spec}`}
        >
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(160deg, rgba(239,68,68,0.08), rgba(229,184,11,0.12) 42%, rgba(16,14,52,0.08))",
            }}
          />
          <div
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage:
                "linear-gradient(rgba(16,14,52,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(16,14,52,0.07) 1px, transparent 1px)",
              backgroundSize: "28px 28px",
            }}
          />
          <div className="absolute bottom-28 left-6 right-6 max-w-xs">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-navy/40">
              Hero image
            </p>
            <p className="mt-2 text-sm font-semibold tracking-tight text-navy/70">
              Drop /images/simcollect/hero.png here
            </p>
            <p className="mt-2 text-xs leading-relaxed text-navy/45">{spec}</p>
          </div>
        </div>
      )}

      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, #faf6f4 0%, #faf6f4 36%, rgba(250,246,244,0.94) 48%, rgba(250,246,244,0.62) 64%, rgba(250,246,244,0.18) 82%, rgba(250,246,244,0.08) 100%)",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(250,246,244,0.55) 0%, transparent 18%, transparent 72%, rgba(250,246,244,0.7) 100%)",
        }}
      />
      <div
        className="absolute inset-0 mix-blend-multiply"
        style={{
          background:
            "linear-gradient(115deg, rgba(239,68,68,0.07) 0%, transparent 38%, rgba(229,184,11,0.08) 100%)",
        }}
      />
    </div>
  );
}

export default CollectHero;
